import { create } from "zustand";
import { supabase } from "./supabase";
import { latestPublishedTracks } from "./catalog";
import { fetchLabelFilterOptions } from "./catalog-facets";
import { fetchCollections } from "./collections";
import { getLabelById } from "./labels";
import type { Track } from "@/types/track";
import type { HomepageCollection, HomepageOrg, HomepageStats } from "@/types/homepage";

/** The "New in the catalog" shelf: the twelve newest published main works. */
export const LATEST_TRACKS_SHOWN = 12;

interface HomepageStatus {
  loading: boolean;
  error: string | null;
  loadedOnce: boolean;
}

interface HomepageState {
  /** Wipe homepage data (logout/account switch). */
  reset: () => void;
  stats: HomepageStats | null;
  /** The newest published main works, newest first (`created_at DESC, id ASC`). */
  latestTracks: Track[];
  /** "Browse by org": one per label with published main works. Empty when that read failed. */
  orgs: HomepageOrg[];
  /** Every usable Collection, in the briefs' order. Empty before the first refresh or when the read failed. */
  collections: HomepageCollection[];
  status: HomepageStatus;
  _generation: number;
  _fetchedAt: number | null;
  fetchHomepage: () => Promise<void>;
}

/** Staleness threshold — refetch if data is older than 5 minutes */
const STALE_MS = 5 * 60 * 1000;

/**
 * The page's stats strip and search pill. The RPC also builds random genre and mood pools from its
 * arguments; empty arrays switch that work off. Its body is not changed: one database serves
 * production and staging.
 */
async function fetchStats(): Promise<HomepageStats> {
  const { data, error } = await supabase.rpc("get_homepage_data", { p_genres: [], p_moods: [] });
  if (error) throw error;
  // RPC returns an opaque Json blob; assert to the app shape at the boundary.
  return (data as unknown as { stats: HomepageStats }).stats;
}

/**
 * The newest published main works, then their labels, so a tile without artwork draws its label's
 * logo in the first paint (NoArtwork starts from `peekLabelById`). A label read that fails fails
 * the shelf, like the track read: the logo would otherwise arrive after the page.
 */
async function fetchLatestTracks(): Promise<Track[]> {
  const { data, error } = await latestPublishedTracks(LATEST_TRACKS_SHOWN);
  if (error) throw error;
  // Rows come back as the tracks table shape; the tiles consume them as the app Track view.
  const tracks = (data ?? []) as unknown as Track[];

  const labelTags = [...new Set(tracks.map((track) => track.label_tag).filter((tag): tag is string => !!tag))];
  const labels = await Promise.all(labelTags.map((tag) => getLabelById(tag)));
  const failed = labels.find((result) => !result.success);
  if (failed) throw new Error(failed.error || "Failed to load the labels of the newest tracks");
  return tracks;
}

/**
 * The org cards: the Tracks page's Label filter options (same read, same exclusions), each with
 * its published main works counted. A GET with `count: "exact"` and one row, never `head: true`
 * (HEAD drew 503 gateway rate-limits). Any failure fails the whole shelf, never a "0".
 */
async function fetchOrgs(): Promise<HomepageOrg[]> {
  const options = await fetchLabelFilterOptions();
  return Promise.all(
    options.map(async (option) => {
      const { count, error } = await supabase
        .from("tracks")
        .select("id", { count: "exact" })
        .limit(1)
        .eq("is_published", true)
        .is("parent_track_id", null)
        .eq("label_tag", option.id);
      if (error) throw error;
      if (count == null) throw new Error(`No track count for ${option.id}`);
      return {
        id: option.id,
        name: option.label,
        logoUrl: option.logoUrl,
        orgType: option.orgType,
        trackCount: count,
      };
    })
  );
}

function errorMessage(reason: unknown, fallback: string): string {
  return reason instanceof Error ? reason.message : (reason as { message?: string } | null)?.message || fallback;
}

export const useHomepageStore = create<HomepageState>((set, get) => ({
  stats: null,
  latestTracks: [],
  orgs: [],
  collections: [],
  status: {
    loading: false,
    error: null,
    loadedOnce: false,
  },
  _generation: 0,
  _fetchedAt: null,

  reset: () =>
    set((state) => ({
      stats: null,
      latestTracks: [],
      orgs: [],
      collections: [],
      status: { loading: false, error: null, loadedOnce: false },
      _generation: state._generation + 1,
      _fetchedAt: null,
    })),

  fetchHomepage: async () => {
    const { status, _fetchedAt, _generation: gen } = get();

    // Skip if already loading
    if (status.loading) return;

    // Skip if data is fresh enough
    if (status.loadedOnce && _fetchedAt && Date.now() - _fetchedAt < STALE_MS) {
      return;
    }

    // A refetch keeps the page that is on screen. With nothing loaded yet (the first load, or Try
    // again after a failure) the page goes back to its skeleton: never an empty catalog and 0 stats.
    set({
      status: { loading: true, error: null, loadedOnce: status.loadedOnce && _fetchedAt !== null },
      _generation: gen + 1,
    });
    const currentGen = gen + 1;

    // One gate: the page paints once, after every read has settled. The stats and the newest
    // tracks are required (the page shows its error instead); the org and Collections shelves
    // are not drawn when their read fails.
    const [stats, latest, orgs, collections] = await Promise.allSettled([
      fetchStats(),
      fetchLatestTracks(),
      fetchOrgs(),
      fetchCollections(),
    ]);

    // Discard if a newer fetch has started
    if (get()._generation !== currentGen) return;

    if (orgs.status === "rejected") console.warn("Could not load the home page's org cards", orgs.reason);
    if (collections.status === "rejected") console.warn("Could not load the home page's Collections", collections.reason);

    if (stats.status === "rejected" || latest.status === "rejected") {
      const reason = stats.status === "rejected" ? stats.reason : (latest as PromiseRejectedResult).reason;
      set({
        status: {
          loading: false,
          error: errorMessage(reason, "Failed to fetch homepage data"),
          loadedOnce: true,
        },
      });
      return;
    }

    set({
      stats: stats.value,
      latestTracks: latest.value,
      orgs: orgs.status === "fulfilled" ? orgs.value : [],
      collections: collections.status === "fulfilled" ? collections.value : [],
      status: {
        loading: false,
        error: null,
        loadedOnce: true,
      },
      _fetchedAt: Date.now(),
    });
  },
}));
